import React, { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Grid,
  Button,
  FormControl,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { Alert, Stack } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';

import PhoneLookup from './PhoneLookup';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './react-datepicker-override.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';

import { Multiselect } from 'react-widgets';
import 'react-widgets/styles.css';

import moment from 'moment-timezone';

import PrimaryButton from './lib/buttons/PrimaryButton';
import { Loading } from '../stories/Loading';
import { FeatureContext } from '../App';

let REFERENCE_GST_INCL_KEY = 'referenceGstInclusive';
let REFERENCE_TARIFF_KEY = 'referenceTariff';
let BOOKING_ID_KEY = 'refBookingId';
let CHECK_IN_TIME_KEY = 'checkInTime';
let CHECK_IN_DATE_KEY = 'checkInDate';
let CHECK_IN_NUMBER_KEY = 'checkInNumber';
let GST_NUMBER_KEY = 'gstNumber';
let ADDRESS_KEY = 'address';
let CITY_STATE_KEY = 'cityState';
let PHONE_NO_KEY = 'phoneNo';
let NOTES_KEY = 'notes';

let FOOD_BILL_PACKAGE = 'Package';
let FOOD_BILL_CASH = 'Cash';
let NONE_SELECTED = 'None';

interface CheckInStateType {
  maxDate: Date;
  maxTime: Date;
  minDate: Date;
  loading: boolean;
  isPhoneLookedUp: boolean;
  phoneNumberFound: boolean;
  availableRooms: [any];
  referenceTariffDisabled: boolean;
}

interface GuestInfoType {
  firstName: string;
  lastName: string;
  address: string;
  cityState: string;
  phoneNo: string;
  photoId?: string;
}

interface FormDataType {
  selectedRooms: any;
  foodBillType: string;
  packageFoodAllowance: number;
  reference: string;
  occupancyType: string;
  referenceTariff: number;
  refGstInclusive: boolean;
  refBookingId?: string;
  checkInDateTime: Date;
  checkInNumber: string;
  gstNumber: string;
  taxpayerType: string;
  notes: string;
}

interface CheckInProps {
  isPhoneLookedUp?: boolean;
  isPhoneNumberFound?: boolean;
  guestInfo?: GuestInfoType;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 'auto',
    textAlign: 'start',
    margin: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingContainer: {
    textAlign: 'start',
    height: 'calc(100VH - 120px)',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    width: '100VW',
  },
  componentTitle: {
    color: '#142966',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontSize: '24px',
    marginBottom: '0px',
  },
  separatorLine: {
    height: '2px',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      width: 'calc(100% - 80px)',
    },
    background: '#004878',
    border: 'none',
  },
  componentSubHeader: {
    color: '#142966',
    textAlign: 'center',
    fontSize: '18px',
    marginBottom: '12px',
    textDecoration: 'underline',
  },
  guestInfoContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
    background: '#D0D0D0',
    borderRadius: '4px',
    marginBottom: '12px',
  },
  guestInfoTable: {
    flex: 1,
  },
  guestInfoSeparator: {
    background: '#004878',
    width: '2px',
    border: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  guestInfoDataKey: {
    width: '120px',
    color: '#004878',
    fontSize: '18px',
    fontWeight: 400,
    padding: '8px',
    verticalAlign: 'top',
  },
  guestInfoDataValue: {
    color: '#004878',
    fontSize: '18px',
    fontWeight: 700,
    padding: '8px',
    verticalAlign: 'top',
  },
  button: {
    width: '100%',
    maxWidth: '320px',
    marginBottom: '12px',
    alignSelf: 'center',
  },
  checkinDetailsContainer: {
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      margin: 'inherit',
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
      marginRight: '40px',
    },
    marginBottom: '12px',
    marginTop: '12px',
  },
  checkinInfoDataKey: {
    marginRight: '28px',
  },
  bookingDetailsContainer: {
    border: '1px solid black',
    [theme.breakpoints.up('sm')]: {
      marginLeft: '40px',
      marginRight: '40px',
    },
    marginBottom: '12px',
  },
  bookingInputGroup: {
    display: 'flex',
    justifyContent: 'center',
    margin: '12px',
  },
  packageLabel: {
    margin: '8px',
    maxWidth: '120px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '10px',
    },
    [theme.breakpoints.up('sm')]: {
      fontSize: '18px',
    },
  },
  datePicker: {
    height: '24px',
    maxWidth: '140px',
    '&>div>input::active': {
      border: 'none',
    },
    '&>div>input': {
      height: '24px',
    },
  },
  sezAlertStack: {
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      marginLeft: '40px',
      marginRight: '40px',
    },
  },
}));

const CheckIn = (props: CheckInProps) => {
  const classes = useStyles();
  const features = useContext(FeatureContext);
  const location = useLocation();
  const navigate = useNavigate();

  const calculateMaxTime = (date: Date) => {
    let isToday = moment(date).isSame(moment(), 'day');
    if (isToday) {
      let nowAddOneHour = moment(new Date()).add({ hours: 1 }).toDate();
      return nowAddOneHour;
    }
    return moment().endOf('day').toDate();
  };

  const initialState: CheckInStateType = {
    maxDate: moment().toDate(),
    maxTime: calculateMaxTime(new Date()),
    minDate: moment().subtract(15, 'days').toDate(),
    loading: true,
    isPhoneLookedUp: false,
    phoneNumberFound: false,
    availableRooms: [{}],
    referenceTariffDisabled: true,
  };

  const initialGuestInfo: GuestInfoType = {
    firstName: '',
    lastName: '',
    address: '',
    cityState: '',
    phoneNo: '',
  };

  const initialFormData: FormDataType = {
    selectedRooms: [],
    foodBillType: NONE_SELECTED,
    referenceTariff: -1,
    refGstInclusive: false,
    packageFoodAllowance: 0,
    occupancyType: 'Double',
    reference: 'direct',
    checkInDateTime: moment().toDate(),
    checkInNumber: '',
    gstNumber: '',
    taxpayerType: '',
    notes: '',
  };

  const [checkinState, setCheckinState] = useState<CheckInStateType>(
    initialState
  );
  const [guestInfo, setGuestInfo] = useState<GuestInfoType>(initialGuestInfo);
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  const [gstMappings, setGstMappings] = useState<[any]>();
  const [selectedCompany, setSelectedCompany] = useState<any>();
  const [liveEnabled, setLiveEnabled] = useState<boolean>(true);
  const [bulkBookingEnabled, setBulkBookingEnabled] = useState<boolean>(false);

  const getAllAvailableRooms = () => {
    setCheckinState((c) => ({ ...c, loading: true }));
    axios.get('/api/getAllFloorsAvailableRooms').then(({ data }) =>
      setCheckinState((c) => ({
        ...c,
        availableRooms: data,
        loading: false,
      }))
    );
  };

  const getAllGstMappings = () => {
    axios.get('/api/fetchGstMappings').then(({ data }) => {
      setGstMappings(data);
    });
  };

  let tickHandler = useRef<NodeJS.Timeout>();

  useEffect(() => {
    tickHandler.current = setInterval(() => {
      if (liveEnabled) {
        setFormData((f) => ({ ...f, checkInDateTime: moment().toDate() }));
      }
    }, 1000);

    return () => {
      if (tickHandler.current) {
        clearInterval(tickHandler.current);
      }
    };
  }, [liveEnabled]);

  useEffect(() => {
    getAllAvailableRooms();
    getAllGstMappings();
    if (location?.state) {
      setCheckinState((c) => ({
        ...c,
        isPhoneLookedUp: location?.state?.isPhoneLookedUp || false,
        phoneNumberFound: location?.state.isPhoneNumberFound || false,
      }));

      if (location?.state.guestInfo) {
        setGuestInfo(location?.state.guestInfo);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (formData.selectedRooms.length === 0) {
      window.alert('Select atleast 1 Room');
    } else {
      setCheckinState((c) => ({ ...c, loading: true }));

      let tAddress = guestInfo.address.trim();
      let tCityState = (guestInfo.cityState || '').trim();
      let tPhoneNumber = guestInfo.phoneNo.trim();
      let tGstNumber = formData.gstNumber;
      let tCheckInNumber = formData.checkInNumber.trim();
      let tNotes = formData.notes.trim();
      let tBookingId = formData.refBookingId;
      let tReferenceTariff = formData.referenceTariff;
      let tRefGstInclusive = formData.refGstInclusive;

      let checkInPostBody = {
        ...formData,
        ...guestInfo,
        [CHECK_IN_TIME_KEY]: moment(formData.checkInDateTime)
          .tz('Asia/Kolkata')
          .format('HH:mm:ss'),
        [CHECK_IN_DATE_KEY]: moment(formData.checkInDateTime)
          .tz('Asia/Kolkata')
          .format('YYYY-MM-DD'),
        [ADDRESS_KEY]: tAddress.length > 0 ? tAddress : null,
        [CITY_STATE_KEY]: tCityState.length > 0 ? tCityState : null,
        [CHECK_IN_NUMBER_KEY]: tCheckInNumber,
        [GST_NUMBER_KEY]: tGstNumber.length > 0 ? tGstNumber : null,
        [PHONE_NO_KEY]: tPhoneNumber,
        [NOTES_KEY]: tNotes.length > 0 ? tNotes : null,
        [REFERENCE_TARIFF_KEY]: tReferenceTariff > 0 ? tReferenceTariff : null,
        [REFERENCE_GST_INCL_KEY]:
          tReferenceTariff > 0 ? tRefGstInclusive : null,
        [BOOKING_ID_KEY]: tBookingId,
      };

      axios
        .post('/api/performCheckIn', checkInPostBody)
        .then((res) => {
          if (res.status === 400) {
            alert(res.data);
          } else {
            setCheckinState({ ...initialState, loading: false });
            setFormData(initialFormData);
            setGuestInfo(initialGuestInfo);
            getAllAvailableRooms();
          }
        })
        .catch((e) => {
          alert('Failed to checkin. Something went wrong!');
          console.log(e);
        });
    }
  };

  const resetCheckin = (e: any) => {
    e.preventDefault();
    setCheckinState((c) => ({
      ...initialState,
      loading: false,
      availableRooms: c.availableRooms,
    }));
  };

  const onPhoneLookupHandler = (err: any, res: any) => {
    if (err) {
      console.log('Error looking up phone number.');
    } else {
      var phoneNumberFound = false;
      let isPhoneLookedUp = true;

      var firstName = '';
      var lastName = '';
      var address = '';
      var cityState = '';
      var phoneNo = res.inputPhoneNo || '';

      if (res.results && res.results.length > 0) {
        let results = res.results[0];
        firstName = results.first_name;
        lastName = results.last_name;
        address = results.address;
        cityState = results.city_state;
        phoneNumberFound = true;

        setCheckinState((c) => ({ ...c, phoneNumberFound, isPhoneLookedUp }));
        setGuestInfo({
          firstName,
          lastName,
          address,
          phoneNo,
          cityState,
        });
      } else {
        navigate('/guestRegister', {
          replace: true,
          state: {
            guestInfo: { ...initialGuestInfo, phoneNo: res.inputPhoneNo },
            isPhoneLookedUp: true,
            isPhoneNumberFound: false,
          },
        });
      }
    }
  };

  const getOccupancyButtonStyle = (isActive: boolean) => {
    let defaultStyle = {
      fontSize: '16px',
      minWidth: '120px',
      marginLeft: '18px',
      marginRight: '18px',
      fontWeight: 600,
    };

    if (isActive) {
      return {
        ...defaultStyle,
        border: '2px solid #004878',
        color: 'white',
        backgroundColor: '#004878',
      };
    }

    return { ...defaultStyle, border: '2px solid black' };
  };

  const getSingleOrDoubleRadioView = () => {
    if (features.singleOccupancyFeatureEnabled) {
      return (
        <FormControl
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            marginTop: '24px',
            marginBottom: '24px',
          }}
        >
          <Button
            style={getOccupancyButtonStyle(formData.occupancyType === 'Single')}
            onClick={(_) =>
              setFormData((f) => ({ ...f, occupancyType: 'Single' }))
            }
          >
            Single
          </Button>
          <Button
            style={getOccupancyButtonStyle(formData.occupancyType === 'Double')}
            onClick={(_) =>
              setFormData((f) => ({ ...f, occupancyType: 'Double' }))
            }
          >
            Double
          </Button>
        </FormControl>
      );
    } else {
      return [];
    }
  };

  const getGstInclusiveOptionIfApplicable = () => {
    if (features.refTariffGstInclFeatureEnabled) {
      return (
        <div className={classes.bookingInputGroup} key="RefGstIncl">
          <label
            style={{
              marginRight: '12px',
              minWidth: '140px',
              alignSelf: 'center',
            }}
          >
            GST Type
          </label>
          <select
            style={{ flex: 1, maxWidth: '300px', height: '36px' }}
            name="gst_type"
            id="gst_type"
            onChange={(e) =>
              setFormData((f) => ({
                ...f,
                refGstInclusive: e.target.value === 'gst_incl',
              }))
            }
            value={formData.refGstInclusive ? 'gst_incl' : 'gst_not_incl'}
          >
            <option value="gst_not_incl">Not Inclusive</option>
            <option value="gst_incl">Inclusive</option>
          </select>
        </div>
      );
    }

    return '';
  };

  const getSEZAlertView = () => {
    return (
      <Stack spacing={0.5} className={classes.sezAlertStack}>
        <Alert
          severity="warning"
          sx={{
            backgroundColor: '#e55039',
            color: 'white',
            fontWeight: 600,
            fontSize: '18px',
            '& .MuiAlert-icon': {
              color: 'white',
            },
            '& .css-1ytlwq5-MuiAlert-icon': {
              fontSize: '30px',
            },
          }}
        >
          SEZ - SPECIAL ECONOMIC ZONE
        </Alert>
        <Alert
          icon={false}
          severity="warning"
          variant="outlined"
          sx={{
            color: '#4b4b4b',
            fontWeight: 600,
            fontSize: '18px',
          }}
        >
          Cannot include food bills in E-Invoice for SEZ.
        </Alert>
      </Stack>
    );
  };

  if (!checkinState.isPhoneLookedUp) {
    return <PhoneLookup phone="" onLookupHandler={onPhoneLookupHandler} />;
  } else {
    if (checkinState.loading) {
      return (
        <div className={classes.loadingContainer}>
          <Loading />
        </div>
      );
    } else {
      return (
        <div className={classes.root}>
          <h2 className={classes.componentTitle}>Check-In</h2>
          <hr className={classes.separatorLine} />
          <h2 className={classes.componentSubHeader}>Guest Info</h2>
          <div className={classes.guestInfoContainer}>
            <table className={classes.guestInfoTable}>
              <tbody>
                <tr>
                  <td className={classes.guestInfoDataKey}>First Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo.firstName}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Last Name:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo.lastName}
                  </td>
                </tr>
                <tr>
                  <td className={classes.guestInfoDataKey}>Phone No:</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo.phoneNo}
                  </td>
                </tr>
              </tbody>
            </table>
            <hr className={classes.guestInfoSeparator} />
            <table className={classes.guestInfoTable}>
              <tbody>
                <tr>
                  <td className={classes.guestInfoDataKey} rowSpan={2}>
                    Address:
                  </td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo.address}
                  </td>
                </tr>
                <tr />
                <tr>
                  <td className={classes.guestInfoDataKey}>City & State</td>
                  <td className={classes.guestInfoDataValue}>
                    {guestInfo.cityState}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <PrimaryButton onClick={resetCheckin} className={classes.button}>
            Reset
          </PrimaryButton>
          <hr className={classes.separatorLine} />

          <div className={classes.checkinDetailsContainer}>
            <Grid container direction="row" alignItems="center" spacing={2}>
              <Grid item xs={6}>
                <label className={classes.checkinInfoDataKey}>
                  Check-In Date:
                </label>
                <DatePicker
                  selected={formData.checkInDateTime}
                  className={classes.datePicker}
                  onChange={(date) => {
                    if (date != null && !Array.isArray(date)) {
                      setFormData((f) => ({ ...f, checkInDateTime: date }));
                    }
                  }}
                  showTimeSelect
                  selectsRange={false}
                  maxDate={checkinState.maxDate}
                  minDate={checkinState.minDate}
                  minTime={moment().startOf('day').toDate()}
                  maxTime={checkinState.maxTime}
                  dateFormat="dd-MMM-yy,  hh:mm aa"
                  required
                  onCalendarOpen={() => {
                    setLiveEnabled(false);
                  }}
                />
                <FontAwesomeIcon
                  icon={faClock}
                  style={{
                    color: liveEnabled ? '#004878' : '#d91e18',
                    marginLeft: '8px',
                    marginTop: '3px',
                  }}
                  onClick={() => {
                    setLiveEnabled(true);
                    setFormData((f) => ({
                      ...f,
                      checkInDateTime: moment().toDate(),
                    }));
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <label className={classes.checkinInfoDataKey}>
                  Check-In No:
                </label>
                <input
                  style={{ height: '24px', maxWidth: '140px' }}
                  name="check-in-number"
                  type="text"
                  id="check-in-number"
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      checkInNumber: e.target.value,
                    }))
                  }
                  value={formData.checkInNumber}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <label className={classes.checkinInfoDataKey}>
                  {`GST Number: ${formData.gstNumber}`}
                </label>
              </Grid>
              <Grid item xs={6}>
                <label
                  className={classes.checkinInfoDataKey}
                  style={{ verticalAlign: 'top', marginRight: '74px' }}
                >
                  Notes:
                </label>
                <textarea
                  style={{ height: '24px', maxWidth: '140px' }}
                  name="notes"
                  id="notes"
                  onChange={(e) =>
                    setFormData((f) => ({ ...f, notes: e.target.value }))
                  }
                  value={formData.notes}
                />
              </Grid>
            </Grid>
          </div>

          {formData.taxpayerType === 'SEZ' ? getSEZAlertView() : []}

          <h2 className={classes.componentSubHeader}>Booking Details</h2>
          <div
            className={classes.bookingDetailsContainer}
            style={{
              borderColor: bulkBookingEnabled ? '#e77f67' : 'black',
              color: bulkBookingEnabled ? '#b71540' : 'black',
            }}
          >
            <div className={classes.bookingInputGroup}>
              <FormControlLabel
                style={{ color: '#142966', fontWeight: 800 }}
                control={
                  <Switch
                    color="secondary"
                    value={bulkBookingEnabled}
                    onChange={() => {
                      setBulkBookingEnabled(!bulkBookingEnabled);
                      setFormData((f) => ({ ...f, selectedRooms: [] }));
                    }}
                  />
                }
                label="Bulk Booking"
              />
            </div>
            <div className={classes.bookingInputGroup}>
              <label
                style={{
                  marginRight: '12px',
                  minWidth: '140px',
                  alignSelf: 'center',
                }}
              >
                Available Rooms
              </label>
              <Multiselect
                style={{ flex: 1, maxWidth: 300 }}
                data={checkinState.availableRooms}
                textField={(info: any) =>
                  info.room_name + ' - ' + info.description
                }
                groupBy={(info) =>
                  features.groupRoomsByBlockFeatureEnabled
                    ? info.block_name
                    : info.floor_str
                }
                onChange={(r) => {
                  if (bulkBookingEnabled || r.length <= 1) {
                    setFormData((f) => ({ ...f, selectedRooms: r }));
                  }
                }}
                value={formData.selectedRooms}
                renderListGroup={({ group }) => {
                  return <span>{group}</span>;
                }}
                renderTagValue={({ item }) => {
                  return (
                    <span
                      style={{
                        color: item.is_clean ? 'black' : '#ff1757',
                        fontWeight: 600,
                      }}
                    >
                      {item.room_name} - {item.description}
                    </span>
                  );
                }}
                renderListItem={({ item }) => {
                  return (
                    <span
                      style={{
                        color: item.is_clean ? 'black' : '#ff4757',
                        fontWeight: item.is_clean ? 400 : 600,
                      }}
                    >
                      {item.room_name} - {item.description}
                    </span>
                  );
                }}
              />
            </div>
            <div className={classes.bookingInputGroup}>
              <label
                style={{
                  marginRight: '12px',
                  minWidth: '140px',
                  alignSelf: 'center',
                }}
              >
                Company Name
              </label>
              <Multiselect
                style={{ flex: 1, maxWidth: 300 }}
                data={gstMappings}
                groupBy={(info) => info.state}
                textField={(info: any) => info.legal_name}
                value={selectedCompany}
                onChange={(r) => {
                  if (r.length === 1) {
                    setSelectedCompany(r);
                    setFormData((f) => ({
                      ...f,
                      gstNumber: r[0]?.gstin,
                      taxpayerType: r[0]?.taxpayer_type,
                    }));
                  } else if (r.length === 0) {
                    setSelectedCompany([]);
                    setFormData((f) => ({
                      ...f,
                      gstNumber: '',
                      taxpayerType: '',
                    }));
                  }
                }}
                renderListGroup={({ group }) => {
                  return (
                    <span
                      style={{
                        color: '#ff1757',
                        fontWeight: 600,
                        textDecoration: 'underline',
                      }}
                    >
                      {group}
                    </span>
                  );
                }}
                renderTagValue={({ item }) => {
                  return (
                    <span
                      style={{
                        color: 'black',
                        fontWeight: 400,
                      }}
                    >
                      {item.legal_name}
                    </span>
                  );
                }}
                renderListItem={({ item }) => {
                  return (
                    <span
                      style={{
                        color: 'black',
                        fontWeight: 400,
                        fontSize: '14px',
                      }}
                    >
                      {item.legal_name}
                    </span>
                  );
                }}
              />
            </div>
            {features.packageFeatureEnabled ||
            features.packageFeatureEnabled ? (
              <div className={classes.bookingInputGroup}>
                {features.packageFeatureEnabled ? (
                  <div
                    style={{
                      marginRight: '24px',
                      minWidth: '88px',
                      marginTop: '18px',
                      marginBottom: '18px',
                    }}
                  >
                    <input
                      style={{
                        height: '18px',
                        width: '18px',
                        margin: 0,
                        verticalAlign: 'middle',
                      }}
                      name="food-bill-type"
                      type="radio"
                      id="package"
                      value="Package"
                      checked={formData.foodBillType === FOOD_BILL_PACKAGE}
                      onChange={(_) =>
                        setFormData((f) => ({
                          ...f,
                          foodBillType: FOOD_BILL_PACKAGE,
                          packageFoodAllowance: 500,
                        }))
                      }
                    />
                    <label className={classes.packageLabel}> Package </label>
                  </div>
                ) : (
                  []
                )}
                {features.foodTypeFeatureEnabled
                  ? [
                      <div
                        style={{
                          marginRight: '24px',
                          minWidth: '88px',
                          marginTop: '18px',
                          marginBottom: '18px',
                        }}
                      >
                        <input
                          style={{
                            height: '18px',
                            width: '18px',
                            margin: 0,
                            verticalAlign: 'middle',
                          }}
                          name="food-bill-type"
                          type="radio"
                          id="cash-food"
                          value="Cash Food"
                          checked={formData.foodBillType === FOOD_BILL_CASH}
                          onChange={(_) =>
                            setFormData((f) => ({
                              ...f,
                              foodBillType: FOOD_BILL_CASH,
                              packageFoodAllowance: 0,
                            }))
                          }
                        />
                        <label className={classes.packageLabel}>
                          {' '}
                          Cash Food{' '}
                        </label>
                      </div>,
                      <div
                        style={{
                          minWidth: '88px',
                          marginTop: '18px',
                          marginBottom: '18px',
                        }}
                      >
                        <input
                          style={{
                            height: '18px',
                            width: '18px',
                            margin: 0,
                            verticalAlign: 'middle',
                          }}
                          name="food-bill-type"
                          type="radio"
                          id="none"
                          value="None"
                          checked={formData.foodBillType === NONE_SELECTED}
                          onChange={(e) =>
                            setFormData((f) => ({
                              ...f,
                              foodBillType: NONE_SELECTED,
                              packageFoodAllowance: 0,
                            }))
                          }
                        />
                        <label className={classes.packageLabel}>None</label>
                      </div>,
                    ]
                  : []}
              </div>
            ) : (
              []
            )}
            {formData.foodBillType === FOOD_BILL_PACKAGE ? (
              <div className={classes.bookingInputGroup}>
                <label
                  style={{
                    marginRight: '12px',
                    minWidth: '140px',
                    alignSelf: 'center',
                  }}
                >
                  Package Amount
                </label>
                <select
                  style={{ flex: 1, maxWidth: '300px', height: '36px' }}
                  name="packageAmount"
                  id="packageAmount"
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      packageFoodAllowance: Number.parseInt(e.target.value),
                    }))
                  }
                  value={formData.packageFoodAllowance.toString()}
                >
                  <option value="500">500</option>
                  <option value="1000">1000</option>
                </select>
              </div>
            ) : (
              ''
            )}
            <div className={classes.bookingInputGroup}>
              <label
                style={{
                  marginRight: '12px',
                  minWidth: '140px',
                  alignSelf: 'center',
                }}
              >
                Reference
              </label>
              <select
                style={{ flex: 1, maxWidth: '300px', height: '36px' }}
                name="reference"
                id="reference"
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    reference: e.target.value,
                  }))
                }
                value={formData.reference}
              >
                <option value="direct">Direct</option>
                <option value="mmt">Make My Trip</option>
                <option value="hotelcloud">Hotel Cloud</option>
                <option value="goibibo">Goibibo</option>
                <option value="yatra">Yatra.com</option>
                <option value="travelguru">Travelguru</option>
                <option value="booking.com">Booking.com</option>
                <option value="cleartrip">Clear Trip</option>
                <option value="other">Other</option>
              </select>
            </div>
            {formData.reference !== 'direct' ? (
              <>
                {getGstInclusiveOptionIfApplicable()}
                <div className={classes.bookingInputGroup}>
                  <label
                    style={{
                      marginRight: '12px',
                      minWidth: '140px',
                      alignSelf: 'center',
                    }}
                  >
                    Reference Tariff
                  </label>
                  <input
                    style={{ flex: 1, maxWidth: '300px', height: '36px' }}
                    name="reference_tariff"
                    id="reference_tariff"
                    pattern="^[1-9][0-9]*$"
                    inputMode="numeric"
                    title="Please enter valid amount Ex: 1000"
                    disabled={formData.reference === 'direct'}
                    onChange={(e) => {
                      let rt = -1;

                      try {
                        rt = Number.parseInt(e.target.value);
                      } catch (error) {
                        alert('Invalid Tariff!');
                        rt = -1;
                      }

                      setFormData((f) => ({
                        ...f,
                        referenceTariff: rt,
                      }));
                    }}
                    value={
                      formData?.referenceTariff > 0
                        ? formData.referenceTariff
                        : ''
                    }
                    required={formData.reference !== 'direct'}
                  />
                </div>
                <div className={classes.bookingInputGroup}>
                  <label
                    style={{
                      marginRight: '12px',
                      minWidth: '140px',
                      alignSelf: 'center',
                    }}
                  >
                    Booking ID
                  </label>
                  <input
                    style={{ flex: 1, maxWidth: '300px', height: '36px' }}
                    name="booking_id"
                    id="booking_id"
                    disabled={formData.reference === 'direct'}
                    onChange={(e) =>
                      setFormData((f) => ({
                        ...f,
                        refBookingId: e.target.value,
                      }))
                    }
                    value={formData.refBookingId || ''}
                    required={formData.refBookingId !== 'direct'}
                  />
                </div>
              </>
            ) : (
              getSingleOrDoubleRadioView()
            )}
          </div>
          <br />
          <PrimaryButton onClick={handleSubmit} className={classes.button}>
            Save
          </PrimaryButton>
        </div>
      );
    }
  }
};

export default CheckIn;
